import { Matrix, Point, Rectangle } from 'pixi.js';
import type { TextStyleRenderer } from '../renderer/TextStyleRenderer';
import { DEFAULT_TEXT_ALPHA } from '../textMetricsHelpers';
import { TextDirection } from '../../../../enums';
import type { WordTokenElement } from './WordTokenElement';
import { measureWord } from './TextMetrics';

export interface TextMetricItemAPI {
    text: string;
    style: TextStyleRenderer;
    ascent: number;
    descent: number;
    fontAscent: number;
    fontDescent: number;
    fontHeight: number;
    advanceWidth: number;
    width: number;
    height: number;
    bounds: Rectangle;
    baselineY: number;
    ascentY: number;
    descentY: number;
    fontAscentY: number;
    fontDescentY: number;
    globalWordElementIndex: number;
}

export enum AnimatableGeometricWordProperties {
    x = 'x',
    y = 'y',
}

export enum AnimatableNonGeometricWordProperties {
    alpha = 'alpha',
}

/**
 * Represents the measurement of a word element.
 * It stores both the normal and highlight style so that it can draw the requested style when needed.
 * Note though that only one style is used to determine the metrics of the word element. That is determined
 * by getStyleForMetrics and is the style returned by the style property.
 */
export class WordElementMetrics implements TextMetricItemAPI {
    #text: string = '';
    #normalStyle: TextStyleRenderer = null;
    #highlightStyle: TextStyleRenderer = null;
    #cachedNormalPassStyle: TextStyleRenderer = null;
    #textMetrics: TextMetrics;
    #wordElementToken: WordTokenElement = null;
    #globalWordElementIndex = -1;
    #isHidden = false;

    // the position of the word element bounds relative to the canvas
    x = 0;
    y = 0;
    // the x advance reference location of the word element
    advanceX = 0;

    constructor(
        text: string,
        normalStyle: TextStyleRenderer,
        highlightStyle: TextStyleRenderer,
        metrics: TextMetrics,
        wordElementToken: WordTokenElement,
    ) {
        this.#text = text;
        this.#normalStyle = normalStyle;
        this.#highlightStyle = highlightStyle;
        this.#textMetrics = metrics;
        this.#wordElementToken = wordElementToken;
    }

    /** Update the style reference */
    updateStyles(normalStyle: TextStyleRenderer, highlightStyle: TextStyleRenderer) {
        this.#normalStyle = normalStyle;
        this.#highlightStyle = highlightStyle;
        this.#cachedNormalPassStyle = null;
    }

    hide() {
        this.#isHidden = true;
    }

    show() {
        this.#isHidden = false;
    }

    /** @internal */
    setGlobalWordElementIndex(value: number) {
        this.#globalWordElementIndex = value;
    }

    partialAdvanceWidth(start: number, end: number): number {
        return (this.#textMetrics.width * (end - start)) / this.#text.length;
    }

    get globalWordElementIndex(): number {
        return this.#globalWordElementIndex;
    }

    get ascentY(): number {
        return this.y - this.ascent;
    }

    get baselineY(): number {
        return this.y;
    }

    get descentY(): number {
        return this.y + this.descent;
    }

    /**
     * Returns the primary style, which is used to determine this element's metrics
     */
    get style(): TextStyleRenderer {
        return getStyleForMetrics(this.highlighted, this.#normalStyle, this.#highlightStyle);
    }

    get text(): string {
        return this.#text;
    }

    get #metrics(): TextMetrics {
        if (this.#isHidden) {
            return {
                actualBoundingBoxAscent: 0,
                actualBoundingBoxDescent: 0,
                actualBoundingBoxLeft: 0,
                actualBoundingBoxRight: 0,
                width: 0,
                fontBoundingBoxAscent: this.#textMetrics.fontBoundingBoxAscent,
                fontBoundingBoxDescent: this.#textMetrics.fontBoundingBoxDescent,
            } as TextMetrics;
        } else {
            return this.#textMetrics;
        }
    }

    get advanceWidth(): number {
        return this.#metrics.width;
    }

    get width(): number {
        return this.#metrics.actualBoundingBoxRight + this.#metrics.actualBoundingBoxLeft;
    }

    get ascent(): number {
        return this.#metrics.actualBoundingBoxAscent;
    }

    get descent(): number {
        return this.#metrics.actualBoundingBoxDescent;
    }

    get height(): number {
        return this.ascent + this.descent;
    }

    get fontAscent(): number {
        return this.#metrics.fontBoundingBoxAscent;
    }

    get fontDescent(): number {
        return this.#metrics.fontBoundingBoxDescent;
    }

    get fontHeight(): number {
        return this.fontAscent + this.fontDescent;
    }

    get fontAscentY(): number {
        return this.y - this.fontAscent;
    }

    get fontDescentY(): number {
        return this.y + this.fontDescent;
    }

    get sourceWordReferenceIndex(): number {
        return this.#wordElementToken.sourceWordReferenceIndex;
    }

    get highlighted(): boolean {
        return this.#wordElementToken.highlighted;
    }

    slice(start: number, end: number): WordTokenElement {
        const clampedStart = Math.max(0, start);
        const clampedEnd = Math.min(end, this.text.length);
        return this.#wordElementToken.slice(clampedStart, clampedEnd);
    }

    get bounds() {
        const textBounds = this._textBounds;
        if (!this.style.shadow) {
            return textBounds;
        }
        const shadowBounds = this._shadowBounds;
        return textBounds.enlarge(shadowBounds);
    }

    get _textBounds(): Rectangle {
        return new Rectangle(
            this.x - this.#metrics.actualBoundingBoxLeft,
            this.y - this.ascent,
            this.width,
            this.height,
        );
    }

    get actualBoundingBoxLeft(): number {
        return this.#metrics.actualBoundingBoxLeft;
    }

    get actualBoundingBoxRight(): number {
        return this.#metrics.actualBoundingBoxRight;
    }

    get _shadowBounds(): Rectangle {
        // apply shadow angle
        const matrix = new Matrix();
        matrix.tx = this.style.shadowDistance;
        matrix.rotate(this.style.shadowAngle);
        const anchor = new Point(0, 0);
        const transformedAnchor = matrix.apply(anchor);

        const textBounds = this._textBounds;
        const shadowBounds = new Rectangle(
            textBounds.x + transformedAnchor.x,
            textBounds.y + transformedAnchor.y,
            textBounds.width,
            textBounds.height,
        );

        // apply shadow blur
        shadowBounds.pad(this.style.shadowBlur, this.style.shadowBlur);

        return shadowBounds;
    }

    setPosition(x: number, y: number) {
        this.x = x;
        this.y = y;
    }

    removeLastCharacter() {
        if (this.#text.length === 0) {
            throw new Error('Cannot remove last character from an empty word element');
        }
        this.#text = this.#text.slice(0, -1);
        this.#textMetrics = measureWord(this.#text, this.style);
    }

    /* The list of properties that can be animated during draw */
    static get animatableProperties() {
        return [
            ...WordElementMetrics.animatableGeometricProperties,
            ...Object.values(AnimatableNonGeometricWordProperties),
        ];
    }

    /**
     * The list of geometry properties that can be animated during draw. There properties affect the position and shape of
     * the element being drawn. The properties in this list will be remapped from the reference resolution to the output.
     **/
    static get animatableGeometricProperties() {
        return Object.values(AnimatableGeometricWordProperties);
    }

    /**
     * Draw the word element on the canvas context. The word element has both the normal and highlight style so
     * the isHighlightPass parameter is used to determine which style to use. Draw will skip drawing in certain cases:
     * 1. If the word element is highlighted and the style is set to not render highlighted elements.
     * 2. If the word element style has an alpha of 0.
     */
    draw(context: CanvasRenderingContext2D, isHighlightPass = false) {
        if (
            this.highlighted &&
            !shouldRenderHighlightedText(isHighlightPass, this.#normalStyle, this.#highlightStyle)
        ) {
            return;
        }

        // calculate animated value
        let x = this.x;
        let y = this.y;
        let alpha = DEFAULT_TEXT_ALPHA;
        const animationConfigurations = isHighlightPass
            ? this.#getAnimationConfigurationsforHighlightPass()
            : this.#getAnimationConfigurationsforNormalPass();
        animationConfigurations.forEach((animation) => {
            switch (animation.property) {
                case 'x':
                    x += animation.computedValues[this.globalWordElementIndex];
                    break;
                case 'y':
                    y += animation.computedValues[this.globalWordElementIndex];
                    break;
                case 'alpha':
                    alpha = animation.computedValues[this.globalWordElementIndex];
                    break;
            }
        });

        // only draw text when visible
        if (alpha > 0) {
            const style = isHighlightPass
                ? this.#getStyleForHighlightPass()
                : this.#getStyleForNormalPass();
            style.setupContextWithStyle(context, alpha);
            context.save();
            context.fillText(this.text, x, y);
            context.restore();
        }
    }

    /**
     * Create a cached wrapper because we may need to generate a combined style by cloning the highlight style,
     * which could be accessed many times during draw.
     */
    #getStyleForNormalPass() {
        if (!this.#cachedNormalPassStyle) {
            this.#cachedNormalPassStyle = getStyleForNormalPass(
                this.highlighted,
                this.#normalStyle,
                this.#highlightStyle,
            );
        }
        return this.#cachedNormalPassStyle;
    }

    #getStyleForHighlightPass() {
        return getStyleForHighlightPass(this.#highlightStyle);
    }

    /**
     * Unfortunately the old TextAsset did not use the normal and highlight styles quite the same way
     * for animations (as compared to the getStyle methods above). So during draw we need to differentiate
     * between animation the rest of the style. So this method returns the animation configuration to use when
     * drawing the highlight pass.
     */
    #getAnimationConfigurationsforHighlightPass() {
        return this.#highlightStyle.animationConfigurations;
    }

    /**
     * Unfortunately the old TextAsset did not use the normal and highlight styles quite the same way
     * for animations (as compared to the getStyle methods above). So during draw we need to differentiate
     * between animation the rest of the style. So this method returns the animation configuration to use when
     * drawing the normal pass.
     */
    #getAnimationConfigurationsforNormalPass() {
        return this.#normalStyle.animationConfigurations;
    }

    /**
     * Utility to calculate the actual (visible) space between two word elements.
     * We can't simply use the advance width, as most of the time the real bounds of a
     * word/character don't equal their advance width.
     * @param previousWord - The word read before the space, or null if this is the first word
     * @param nextWord - The word read after the space, or null if this is the last word
     * @param advanceWidth - The advance width of a space
     * @param textDirection - The text direction (LTR or RTL)
     */
    static calculateSpaceBetweenWords(
        previousWord: WordElementMetrics | null,
        nextWord: WordElementMetrics | null,
        advanceWidth: number,
        textDirection: TextDirection,
    ): number {
        if (textDirection === TextDirection.LTR) {
            if (!previousWord && nextWord) {
                return nextWord.bounds.x;
            }
            if (previousWord && !nextWord) {
                const previousWordEndX = previousWord.bounds.x + previousWord.bounds.width;
                const advanceX = previousWord.advanceX + previousWord.advanceWidth;
                return advanceX - previousWordEndX + advanceWidth;
            }
            if (previousWord && nextWord) {
                const previousWordEndX = previousWord.bounds.x + previousWord.bounds.width;
                return nextWord.bounds.x - previousWordEndX;
            }
            return advanceWidth;
        } else {
            if (!previousWord && nextWord) {
                const nextWordEndX = nextWord.bounds.x + nextWord.bounds.width;
                const advanceX = nextWord.advanceX + nextWord.advanceWidth;
                return advanceX - nextWordEndX + advanceWidth;
            }
            if (previousWord && !nextWord) {
                const advanceX = previousWord.advanceX;
                return previousWord.bounds.x - advanceX + advanceWidth;
            }
            if (previousWord && nextWord) {
                const nextWordEndX = nextWord.bounds.x + nextWord.bounds.width;
                return previousWord.bounds.x - nextWordEndX;
            }
            return advanceWidth;
        }
    }
}

/**
 * Based on highlight info, returns the style an element should use for metrics. This will return the
 * style that should be visible at "steady state". So, for example, if there's a highlight and the highlight style is
 * enabled for highlights, the highlight style will be returned.
 */
export function getStyleForMetrics(
    isWordElementHighlighted: boolean,
    normalStyle: TextStyleRenderer,
    highlightStyle: TextStyleRenderer,
) {
    // If the highlight pass should render, then always use the highlight style (as that will be on top of the normal style)
    if (
        isWordElementHighlighted &&
        shouldRenderHighlightedText(true, normalStyle, highlightStyle)
    ) {
        // the element is highlighted and the highlight style should render the element
        return getStyleForHighlightPass(highlightStyle);
    }
    // The highlight style is not rendering the highlight, so the normal pass will dictate the style
    return getStyleForNormalPass(isWordElementHighlighted, normalStyle, highlightStyle);
}

/**
 * Based on which render pass is being used, determines if a highlighted word element should be drawn
 */
function shouldRenderHighlightedText(
    isHighlightPass: boolean,
    normalStyle: TextStyleRenderer,
    highlightStyle: TextStyleRenderer,
): boolean {
    return isHighlightPass ? highlightStyle.renderHighlighted : normalStyle.renderHighlighted;
}

/**
 * For the highlight pass, we always use the highlight style to render.
 */
function getStyleForHighlightPass(highlightStyle: TextStyleRenderer): TextStyleRenderer {
    return highlightStyle;
}

/**
 * For the normal pass, we use the normal style to render non-highlighted text. For highlighted text, we use the
 * highlight style but depending on the normal style's renderHighlightedColor property, we may use the normal style
 * colors.
 */
function getStyleForNormalPass(
    isWordElementHighlighted: boolean,
    normalStyle: TextStyleRenderer,
    highlightStyle: TextStyleRenderer,
): TextStyleRenderer {
    if (!isWordElementHighlighted) {
        // For non-highlighted text always use the normal style
        return normalStyle;
    }
    if (normalStyle.renderHighlighted && normalStyle.renderHighlightedColor) {
        // For highlighted text, if the normal style is set to render highlighted text, use the highlight style
        return highlightStyle;
    }

    // For highlighted text, we use the highlight style but with the colors from the normal style
    const highlightStyleWithColorsFromNormalStyle = highlightStyle.clone();
    highlightStyleWithColorsFromNormalStyle.fill = normalStyle.fill;
    highlightStyleWithColorsFromNormalStyle.shadowColor = normalStyle.shadowColor;
    return highlightStyleWithColorsFromNormalStyle;
}
